import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiChevronRight, FiCheck, FiArrowRight } from 'react-icons/fi';
import PopupForm from '../pages/PopupForm';
import ServiceNav from './ServiceNav';
import { ServiceCta } from './CtaSystem';
import './ServicePageLayout.css';

const ServicePageLayout = ({ service }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!service) return null;

  const renderIcon = (iconType) => {
    switch (iconType) {
      case 'pin':
        return <img src="/pin-img.png" alt="" className="sp-feature-card-img" />;
      case 'check':
        return <FiCheck />;
      case 'arrow':
        return <FiArrowRight />;
      default:
        return <FiCheck />;
    }
  };

  const renderSection = (section, index) => {
    switch (section.type) {
      case 'paragraph':
        return (
          <motion.section
            key={index}
            className="sp-section sp-paragraph-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
          >
            <div className="sp-container">
              {section.heading && <h3 className="sp-subheading">{section.heading}</h3>}
              <p className="sp-section-text">{section.content}</p>
            </div>
          </motion.section>
        );

      case 'feature-grid':
        return (
          <motion.section
            key={index}
            className="sp-section sp-feature-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
          >
            <div className="sp-container">
              {section.heading && <h3 className="sp-subheading">{section.heading}</h3>}
              <div className="sp-feature-grid">
                {section.items.map((item, i) => (
                  <motion.div
                    key={i}
                    className="sp-feature-card"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (i % 6) * 0.06 }}
                  >
                    <div className="sp-feature-card-icon">
                      {renderIcon(section.icon)}
                    </div>
                    <div className="sp-feature-card-content">
                      <h4 className="sp-feature-card-title">{item.title}</h4>
                      <p className="sp-feature-card-desc">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        );

      case 'ending':
        return (
          <motion.section
            key={index}
            className="sp-section sp-ending-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
          >
            <div className="sp-container">
              <p className="sp-ending-text">{section.content}</p>
            </div>
          </motion.section>
        );

      default:
        return null;
    }
  };

  return (
    <div className="sp-page">
      {/* Breadcrumb */}
      <div className="sp-breadcrumb-bar">
        <div className="sp-container">
          <nav className="sp-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="sp-breadcrumb-link">Home</Link>
            <FiChevronRight className="sp-breadcrumb-sep" />
            <Link to="/services" className="sp-breadcrumb-link">Services</Link>
            <FiChevronRight className="sp-breadcrumb-sep" />
            <span className="sp-breadcrumb-current">{service.shortTitle}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="sp-hero">
        <div className="sp-hero-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/service-bg.png)` }} />
        <div className="sp-container sp-hero-inner">
          <motion.div
            className="sp-hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="sp-hero-kicker-wrap">
              <span className="sp-hero-kicker">{service.kicker}</span>
            </div>
            <h1 className="sp-hero-title">{service.title}</h1>
            <p className="sp-hero-desc">{service.heroDesc}</p>
            <div className="sp-hero-actions">
              <button className="sp-btn sp-btn-primary" onClick={() => setIsPopupOpen(true)}>
                Discuss Your Requirements
              </button>
              <span className="sp-btn sp-btn-secondary sp-btn-disabled" title="Coming Soon">
                View PDF
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      {service.sections.map((section, index) => renderSection(section, index))}

      {/* CTA */}
      <ServiceCta
        eyebrow={service.kicker}
        heading="Talk to DBH About Your Requirements"
        primaryBtnText="Discuss Your Requirements"
        onPrimaryClick={() => setIsPopupOpen(true)}
        secondaryBtnText="Contact Us"
        secondaryBtnLink="/contact-us"
      />

      {/* Service Navigation */}
      <ServiceNav currentPath={service.route} />

      {/* Popup Form */}
      <PopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default ServicePageLayout;
